import { useState, useEffect, useMemo, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardList, Plus, ChevronRight, X, ArrowLeft, Filter, Save,
  CheckCircle2, AlertTriangle, ChevronLeft, List, Grid3x3, Trash2,
  Loader2, Search, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// Build a vendor-ready CSV from the count's under-PAR lines. Grouped by
// vendor so the user can shoot it off to multiple vendors without
// re-sorting.
function downloadOrderCsv(count) {
  const lines = [["Product", "SKU", "Vendor", "Category", "PAR", "Counted", "To Order", "Unit Cost", "Extended Cost"]];
  const items = (count.lines || [])
    .filter(l => l.counted_qty !== null && (l.par || 0) > 0 && l.counted_qty < l.par)
    .sort((a, b) => (a.vendor || "").localeCompare(b.vendor || "") || (a.product_name || "").localeCompare(b.product_name || ""));
  items.forEach(l => {
    const toOrder = Math.max(0, l.par - l.counted_qty);
    const ext = (toOrder * (l.cost || 0)).toFixed(2);
    lines.push([
      l.product_name || "",
      l.sku || "",
      l.vendor || "",
      l.category || "",
      String(l.par || 0),
      String(l.counted_qty),
      String(toOrder),
      String(l.cost || 0),
      ext,
    ]);
  });
  if (lines.length === 1) {
    toast.info("No items are under PAR yet.");
    return;
  }
  const csv = lines
    .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `order-from-count-${(count.name || count.count_id).replace(/[^a-z0-9-]+/gi, "_")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${lines.length - 1} item${lines.length - 1 === 1 ? "" : "s"} to CSV`);
}

// ─── Counts tab inside Inventory ───────────────────────────────────────
// Owns its own data fetch. When entering a count session it swaps to
// the counting interface. Lines autosave on blur.
export default function InventoryCountsTab({ products }) {
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCountId, setActiveCountId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const r = await api.get("/inventory/counts");
      setCounts(r.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // Resume the most-recent in-progress count automatically when there's
  // exactly one (typical case). Skip if multiple — let the user pick.
  useEffect(() => {
    if (loading || activeCountId) return;
    const inProg = counts.filter(c => c.status === "in_progress");
    if (inProg.length === 1) {
      setActiveCountId(inProg[0].count_id);
    }
  }, [loading, counts, activeCountId]);

  if (activeCountId) {
    return (
      <CountingSession
        countId={activeCountId}
        products={products}
        onExit={() => { setActiveCountId(null); fetchCounts(); }}
      />
    );
  }

  return (
    <div data-testid="inventory-counts-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">Inventory Counts</h2>
          <p className="text-xs text-slate-500 mt-0.5">Physical stock checks. Autosaved as you go.</p>
        </div>
        <Button data-testid="new-count-btn" onClick={() => setShowNewModal(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-full h-10 px-5">
          <Plus className="w-4 h-4 mr-1.5" /> Start New Count
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
      ) : counts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-2xl">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-900">No counts yet</h3>
          <p className="text-sm text-slate-500 mt-1">Tap <span className="font-medium">Start New Count</span> to begin your first stocktake.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#FAFAFA] border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Count</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Products</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Started</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">By</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {counts.map(c => (
                <tr key={c.count_id} data-testid={`count-row-${c.count_id}`}
                  onClick={() => setActiveCountId(c.count_id)}
                  className="border-b border-slate-100 last:border-b-0 hover:bg-[#FAFAFA] cursor-pointer transition-colors">
                  <td className="px-6 py-3.5">
                    <p className="text-sm font-medium text-slate-900">{c.name}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {[
                        c.filters?.category && `Category: ${c.filters.category}`,
                        c.filters?.vendor && `Vendor: ${c.filters.vendor}`,
                        c.filters?.location_id && `Location-scoped`,
                      ].filter(Boolean).join(" · ") || "All products"}
                    </p>
                  </td>
                  <td className="px-3 py-3.5">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-3 py-3.5 text-right text-sm text-slate-700">{c.total_products || 0}</td>
                  <td className="px-3 py-3.5 text-sm text-slate-500">
                    {c.started_at ? new Date(c.started_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-3 py-3.5 text-sm text-slate-500">{c.started_by_name || "—"}</td>
                  <td className="px-3 py-3.5 text-right">
                    <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNewModal && (
        <NewCountModal
          products={products}
          onClose={() => setShowNewModal(false)}
          onCreated={(id) => { setShowNewModal(false); fetchCounts(); setActiveCountId(id); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    in_progress: { label: "In progress", bg: "bg-[#EAF0F6]", fg: "text-blue-700" },
    applied: { label: "Applied", bg: "bg-[#E8F0E9]", fg: "text-emerald-700" },
    cancelled: { label: "Cancelled", bg: "bg-[#F3F3F3]", fg: "text-slate-500" },
  };
  const m = map[status] || map.cancelled;
  return <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full ${m.bg} ${m.fg}`}>{m.label}</span>;
}

// ─── New Count Modal ───────────────────────────────────────────────────
function NewCountModal({ products, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("all");
  const [vendor, setVendor] = useState("all");
  const [saving, setSaving] = useState(false);

  // Pull distinct categories/vendors from products so the user only sees
  // values that actually have inventory rows behind them.
  const categories = useMemo(() => Array.from(new Set((products || []).map(p => p.category).filter(Boolean))).sort(), [products]);
  const vendors = useMemo(() => Array.from(new Set((products || []).map(p => p.vendor).filter(Boolean))).sort(), [products]);

  // Live preview: how many products will be in this count?
  const productCount = useMemo(() => {
    return (products || []).filter(p => {
      if (category !== "all" && p.category !== category) return false;
      if (vendor !== "all" && p.vendor !== vendor) return false;
      return true;
    }).length;
  }, [products, category, vendor]);

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category: category === "all" ? "" : category,
        vendor: vendor === "all" ? "" : vendor,
      };
      const r = await api.post("/inventory/counts", payload);
      toast.success("Count started");
      onCreated?.(r.data.count_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start count");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="new-count-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg">Start a new count</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name (optional)</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Monthly count — June"
              data-testid="new-count-name" className="mt-1 h-10 border-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1 h-10 border-slate-200" data-testid="new-count-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Vendor</label>
              <Select value={vendor} onValueChange={setVendor}>
                <SelectTrigger className="mt-1 h-10 border-slate-200" data-testid="new-count-vendor">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All vendors</SelectItem>
                  {vendors.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">Will include</p>
            <p className="font-['Outfit'] text-2xl font-semibold text-slate-900 mt-1">{productCount}</p>
            <p className="text-[11px] text-slate-500">product{productCount === 1 ? "" : "s"}</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-200 rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving || productCount === 0}
              data-testid="new-count-submit"
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-5">
              {saving ? "Starting..." : "Start counting"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Counting Session ──────────────────────────────────────────────────
function CountingSession({ countId, products, onExit }) {
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "card"
  const [cardIdx, setCardIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("category"); // "category" | "vendor" | "none"
  const [savingLines, setSavingLines] = useState({}); // product_id -> saving flag
  const [applyOpen, setApplyOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/inventory/counts/${countId}`);
      setCount(r.data);
    } catch (err) {
      toast.error("Failed to load count");
    } finally { setLoading(false); }
  }, [countId]);

  useEffect(() => { load(); }, [load]);

  // Backfill `par` from the live products list for legacy sessions that
  // were started before we started snapshotting PAR on the count line.
  // We do this in-memory only — the saved doc stays as-is.
  const countWithPar = useMemo(() => {
    if (!count) return null;
    if (!products?.length) return count;
    const parByProduct = new Map(
      products.map(p => [p.product_id, Number(p.reorder_point || p.low_stock_threshold || 0)])
    );
    return {
      ...count,
      lines: (count.lines || []).map(l => ({
        ...l,
        par: l.par && l.par > 0 ? l.par : (parByProduct.get(l.product_id) || 0),
      })),
    };
  }, [count, products]);

  const updateLine = async (productId, value) => {
    // value is the raw string from the input — parse to int or null
    const counted = value === "" || value === null ? null : Math.max(0, parseInt(value, 10) || 0);
    // Optimistic in-place mutation; revert on failure.
    setCount(prev => prev && {
      ...prev,
      lines: prev.lines.map(l => l.product_id === productId
        ? { ...l, counted_qty: counted, counted_at: counted !== null ? new Date().toISOString() : null }
        : l),
    });
    setSavingLines(s => ({ ...s, [productId]: true }));
    try {
      await api.put(`/inventory/counts/${countId}/line/${productId}`, { counted_qty: counted });
    } catch (err) {
      toast.error("Couldn't autosave that line");
      // Reload to get back to truth
      load();
    } finally {
      setSavingLines(s => { const n = { ...s }; delete n[productId]; return n; });
    }
  };

  const cancelCount = async () => {
    if (!window.confirm("Cancel this count? All progress will be lost.")) return;
    try {
      await api.delete(`/inventory/counts/${countId}`);
      toast.success("Count cancelled");
      onExit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  if (loading || !countWithPar) {
    return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;
  }

  return <CountingSessionInner
    count={countWithPar} updateLine={updateLine} cancelCount={cancelCount}
    view={view} setView={setView} cardIdx={cardIdx} setCardIdx={setCardIdx}
    search={search} setSearch={setSearch} groupBy={groupBy} setGroupBy={setGroupBy}
    savingLines={savingLines} applyOpen={applyOpen} setApplyOpen={setApplyOpen}
    load={load} onExit={onExit}
  />;
}

function CountingSessionInner({ count, updateLine, cancelCount, view, setView, cardIdx, setCardIdx, search, setSearch, groupBy, setGroupBy, savingLines, applyOpen, setApplyOpen, load, onExit }) {
  const isDone = count.status !== "in_progress";

  // Filtered + grouped lines for the list view
  const filtered = (count.lines || []).filter(l =>
    !search || l.product_name?.toLowerCase().includes(search.toLowerCase()) || l.sku?.toLowerCase().includes(search.toLowerCase())
  );
  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ label: "All products", lines: filtered }];
    const map = new Map();
    filtered.forEach(l => {
      const key = (l[groupBy] || "Uncategorized");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([label, lines]) => ({ label, lines }));
  }, [filtered, groupBy]);

  // Progress
  const total = count.lines.length;
  const counted = count.lines.filter(l => l.counted_qty !== null).length;
  // Lines that are under PAR — these are the items the user will need to
  // order. (par > 0 required, else we have no order target.)
  const underPar = count.lines.filter(l =>
    l.counted_qty !== null && (l.par || 0) > 0 && l.counted_qty < l.par
  );
  // Sum of units that need to be reordered.
  const totalToOrder = underPar.reduce((s, l) => s + Math.max(0, (l.par || 0) - l.counted_qty), 0);
  const progressPct = total > 0 ? Math.round((counted / total) * 100) : 0;

  // Card view navigation
  // (no precomputation — the card view sub-component owns its own logic)

  return (
    <div data-testid="counting-session">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 -mx-6 lg:-mx-8 px-6 lg:px-8 py-3 mb-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button data-testid="counting-back" onClick={onExit}
              className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center flex-shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{count.name}</p>
              <p className="text-[11px] text-slate-500">
                {counted} of {total} counted{" "}
                {underPar.length > 0 && (
                  <span className="text-rose-600 font-semibold">
                    · {underPar.length} under PAR ({totalToOrder} to order)
                  </span>
                )}
                {Object.keys(savingLines).length > 0 && <span className="text-blue-600 ml-2">· Saving...</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="hidden md:flex items-center gap-1 bg-[#FAFAFA] rounded-full p-1">
              <button data-testid="view-list" onClick={() => setView("list")}
                className={`h-7 px-3 text-xs font-medium rounded-full transition-colors ${view === "list" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}>
                <List className="w-3 h-3 inline mr-1" /> List
              </button>
              <button data-testid="view-card" onClick={() => setView("card")}
                className={`h-7 px-3 text-xs font-medium rounded-full transition-colors ${view === "card" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}`}>
                <Grid3x3 className="w-3 h-3 inline mr-1" /> One at a time
              </button>
            </div>
            {!isDone && (
              <>
                <Button variant="outline" size="sm" data-testid="counting-export-order"
                  onClick={() => downloadOrderCsv(count)}
                  disabled={underPar.length === 0}
                  className="border-slate-200 hover:bg-slate-50 rounded-full h-9">
                  <Download className="w-3.5 h-3.5 mr-1" /> Order CSV
                </Button>
                <Button variant="ghost" size="sm" onClick={cancelCount} data-testid="counting-cancel"
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-full h-9">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
                <Button data-testid="counting-apply" onClick={() => setApplyOpen(true)}
                  disabled={counted === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-9 px-5">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Apply Count
                </Button>
              </>
            )}
            {isDone && (
              <Button variant="outline" size="sm" data-testid="counting-export-order-done"
                onClick={() => downloadOrderCsv(count)}
                disabled={underPar.length === 0}
                className="border-slate-200 hover:bg-slate-50 rounded-full h-9">
                <Download className="w-3.5 h-3.5 mr-1" /> Order CSV
              </Button>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-1.5 bg-[#FAFAFA] rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Toolbar */}
      {view === "list" && (
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              data-testid="counting-search"
              placeholder="Search this count..." className="pl-10 h-9 border-slate-200" />
          </div>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="w-44 h-9 border-slate-200" data-testid="counting-groupby">
              <Filter className="w-3 h-3 mr-1.5" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="category">Group by category</SelectItem>
              <SelectItem value="vendor">Group by vendor</SelectItem>
              <SelectItem value="none">No grouping</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Content */}
      {view === "list" ? (
        <div className="space-y-6">
          {grouped.map(group => (
            <div key={group.label} data-testid={`count-group-${group.label}`}>
              {groupBy !== "none" && (
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">{group.label}</h3>
                  <span className="text-[11px] text-slate-400">{group.lines.length} item{group.lines.length === 1 ? "" : "s"}</span>
                </div>
              )}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#FAFAFA] border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600">Product</th>
                      <th className="text-right px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 w-20">PAR</th>
                      <th className="text-right px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 w-28">Counted</th>
                      <th className="text-right px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 w-32">vs PAR</th>
                      <th className="text-right px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 w-24">To order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.lines.map(l => (
                      <CountLineRow key={l.product_id} line={l}
                        saving={!!savingLines[l.product_id]}
                        disabled={isDone}
                        onUpdate={updateLine} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-400">No products match "{search}".</div>
          )}
        </div>
      ) : (
        <CardView lines={filtered} idx={cardIdx} setIdx={setCardIdx} onUpdate={updateLine}
          savingLines={savingLines} disabled={isDone} />
      )}

      {applyOpen && (
        <ApplyCountDialog count={count} onClose={() => setApplyOpen(false)} onApplied={() => { setApplyOpen(false); load(); }} />
      )}
    </div>
  );
}

// ─── List-view row with autosave-on-blur input ─────────────────────────
function CountLineRow({ line, saving, disabled, onUpdate }) {
  const [value, setValue] = useState(line.counted_qty === null ? "" : String(line.counted_qty));
  // Resync local state when the upstream line value changes (e.g. after
  // an undo or reload).
  useEffect(() => {
    setValue(line.counted_qty === null ? "" : String(line.counted_qty));
  }, [line.counted_qty]);

  // ─ Metrics now anchored on PAR, not system-expected stock. The whole
  // point of this view is "how much do we need to order?".
  const par = line.par || 0;
  const counted = line.counted_qty;
  const hasCount = counted !== null;
  const vsPar = hasCount && par > 0 ? counted - par : null;       // signed: under-par is negative
  const toOrder = hasCount && par > 0 ? Math.max(0, par - counted) : null;

  const tone = vsPar === null ? "neutral"
    : vsPar === 0 ? "match"
    : vsPar < 0 ? "short" : "over";
  const toneClass = {
    neutral: "text-slate-300",
    match: "text-emerald-600",
    short: "text-rose-600 font-semibold",
    over: "text-amber-600 font-semibold",
  }[tone];

  return (
    <tr data-testid={`count-line-${line.product_id}`}
      className={`border-b border-slate-100 last:border-b-0 ${hasCount ? "bg-emerald-50/30" : ""}`}>
      <td className="px-4 py-2.5">
        <p className="text-sm font-medium text-slate-900">{line.product_name}</p>
        <p className="text-[11px] text-slate-400 font-mono">{line.sku || "—"}</p>
      </td>
      <td className="px-2 py-2.5 text-right text-sm text-slate-500 tabular-nums">
        {par > 0 ? par : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-2 py-2.5 text-right">
        <div className="flex items-center justify-end gap-1">
          {saving && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
          <Input
            type="number" inputMode="numeric" min={0}
            disabled={disabled}
            value={value}
            onChange={e => setValue(e.target.value.replace(/[^\d]/g, ""))}
            onBlur={() => onUpdate(line.product_id, value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
            data-testid={`count-input-${line.product_id}`}
            placeholder="—"
            className="w-20 h-8 text-sm text-right border-slate-200 rounded-md tabular-nums"
          />
        </div>
      </td>
      <td className={`px-2 py-2.5 text-right text-sm tabular-nums ${toneClass}`}>
        {vsPar === null
          ? "—"
          : vsPar === 0
            ? "✓ at PAR"
            : vsPar > 0
              ? `+${vsPar} over`
              : `${vsPar} under`}
      </td>
      <td className="px-2 py-2.5 text-right text-sm tabular-nums font-semibold">
        {toOrder === null ? (
          <span className="text-slate-300">—</span>
        ) : toOrder === 0 ? (
          <span className="text-emerald-600">0</span>
        ) : (
          <span className="text-rose-700">{toOrder}</span>
        )}
      </td>
    </tr>
  );
}

// ─── Card view — one product at a time ─────────────────────────────────
function CardView({ lines, idx, setIdx, onUpdate, savingLines, disabled }) {
  const safeIdx = Math.max(0, Math.min(idx, Math.max(lines.length - 1, 0)));
  const line = lines[safeIdx] || null;
  const [value, setValue] = useState(line && line.counted_qty !== null ? String(line.counted_qty) : "");
  useEffect(() => {
    if (line) setValue(line.counted_qty === null ? "" : String(line.counted_qty));
  }, [line]);

  if (!lines.length || !line) {
    return <div className="text-center py-16 text-sm text-slate-400">No products to count.</div>;
  }

  const commit = () => onUpdate(line.product_id, value);
  const next = () => { commit(); setIdx(i => Math.min(i + 1, lines.length - 1)); };
  const prev = () => { commit(); setIdx(i => Math.max(i - 1, 0)); };

  const par = line.par || 0;
  const counted = line.counted_qty;
  const hasCount = counted !== null;
  const vsPar = hasCount && par > 0 ? counted - par : null;
  const toOrder = hasCount && par > 0 ? Math.max(0, par - counted) : null;

  return (
    <div className="max-w-md mx-auto" data-testid="counting-card-view">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{line.category || "Uncategorized"}{line.vendor ? ` · ${line.vendor}` : ""}</p>
        <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mt-1">{line.product_name}</h3>
        <p className="text-xs text-slate-400 font-mono mt-1">{line.sku || "—"}</p>

        <div className="mt-6 bg-[#FAFAFA] rounded-xl p-4 text-center">
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">PAR (target)</p>
          <p className="font-['Outfit'] text-4xl font-light text-slate-900 mt-1 tabular-nums">
            {par > 0 ? par : <span className="text-slate-300">—</span>}
          </p>
        </div>

        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Your count</label>
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="card-decrement" disabled={disabled}
              onClick={() => { const n = Math.max(0, (parseInt(value || "0", 10)) - 1); setValue(String(n)); onUpdate(line.product_id, String(n)); }}
              className="w-12 h-12 rounded-xl border border-slate-200 text-2xl text-slate-600 hover:bg-slate-50">−</button>
            <Input
              type="number" inputMode="numeric" min={0}
              disabled={disabled}
              value={value}
              onChange={e => setValue(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={commit}
              data-testid="card-input"
              placeholder="—"
              className="flex-1 h-12 text-center font-['Outfit'] text-2xl border-slate-200 rounded-xl tabular-nums"
            />
            <button data-testid="card-increment" disabled={disabled}
              onClick={() => { const n = (parseInt(value || "0", 10)) + 1; setValue(String(n)); onUpdate(line.product_id, String(n)); }}
              className="w-12 h-12 rounded-xl border border-emerald-200 bg-emerald-500 text-white text-2xl hover:bg-emerald-600">+</button>
          </div>
        </div>

        {/* vs PAR + To-order summary, only when both par and count present */}
        {hasCount && par > 0 && (
          <div className={`mt-4 px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-between ${
            vsPar < 0 ? "bg-rose-50 text-rose-700"
            : vsPar > 0 ? "bg-amber-50 text-amber-700"
            : "bg-emerald-50 text-emerald-700"
          }`}>
            <div className="flex items-center gap-2">
              {vsPar < 0 ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              <span>
                {vsPar === 0 ? "Right at PAR" : vsPar > 0 ? `${vsPar} over PAR` : `${Math.abs(vsPar)} under PAR`}
              </span>
            </div>
            {toOrder > 0 && <span>Order {toOrder}</span>}
          </div>
        )}
        {hasCount && par === 0 && (
          <p className="mt-4 text-[11px] text-slate-400 text-center">No PAR set for this product — can't suggest an order qty.</p>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-5">
        <Button variant="outline" onClick={prev} disabled={safeIdx === 0} className="rounded-full px-4 h-10 border-slate-200">
          <ChevronLeft className="w-4 h-4 mr-1" /> Previous
        </Button>
        <p className="text-xs text-slate-500">{safeIdx + 1} of {lines.length}{savingLines[line.product_id] && " · Saving…"}</p>
        <Button onClick={next} disabled={safeIdx === lines.length - 1} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-4 h-10">
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ─── Apply Confirmation ────────────────────────────────────────────────
function ApplyCountDialog({ count, onClose, onApplied }) {
  const [mode, setMode] = useState("all");
  const [saving, setSaving] = useState(false);

  const counted = (count.lines || []).filter(l => l.counted_qty !== null);
  // PAR-anchored bucketing — what the user actually cares about.
  const atOrAbovePar = counted.filter(l => (l.par || 0) > 0 && l.counted_qty >= l.par);
  const underPar = counted.filter(l => (l.par || 0) > 0 && l.counted_qty < l.par);
  const noPar = counted.filter(l => !l.par || l.par <= 0);
  // System-stock variances — still needed because applying always
  // syncs stock to the counted value. We surface this so the user
  // knows they're also clearing system drift.
  const stockVariances = counted.filter(l => l.counted_qty !== l.expected_qty);
  const uncounted = (count.lines || []).length - counted.length;
  const totalToOrder = underPar.reduce((s, l) => s + Math.max(0, l.par - l.counted_qty), 0);

  const apply = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/inventory/counts/${count.count_id}/apply`, { mode });
      toast.success(`Applied ${r.data.applied_count} line${r.data.applied_count === 1 ? "" : "s"}`);
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="apply-count-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg">Finish count</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* PAR-focused summary — primary numbers */}
          <div className="bg-[#FAFAFA] rounded-xl p-3 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">At / above PAR</p>
              <p className="font-['Outfit'] text-xl text-emerald-600 font-semibold">{atOrAbovePar.length}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Under PAR</p>
              <p className="font-['Outfit'] text-xl text-rose-600 font-semibold">{underPar.length}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Uncounted</p>
              <p className="font-['Outfit'] text-xl text-slate-400 font-semibold">{uncounted}</p>
            </div>
          </div>

          {/* Total units to order — the headline number this whole flow exists for */}
          {underPar.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Need to order</p>
                <p className="font-['Outfit'] text-2xl font-semibold text-rose-700">{totalToOrder} <span className="text-sm text-rose-500 font-normal">unit{totalToOrder === 1 ? "" : "s"}</span></p>
              </div>
              <button data-testid="apply-dialog-download-csv"
                onClick={() => downloadOrderCsv(count)}
                className="text-xs font-semibold text-rose-700 px-3 h-8 rounded-full bg-white border border-rose-200 hover:bg-rose-100 flex items-center gap-1">
                <Download className="w-3 h-3" /> Order CSV
              </button>
            </div>
          )}

          {/* What "Apply" does — small reassurance about the side-effects */}
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Applying will update each product's <strong>system stock</strong> to match your physical count.
            {noPar.length > 0 && <> {noPar.length} product{noPar.length === 1 ? "" : "s"} have no PAR set and can't be measured against it.</>}
          </div>

          {stockVariances.length > 0 && (
            <div className="space-y-2">
              <label className="flex items-start gap-2 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-[#FAFAFA]">
                <input type="radio" name="mode" value="all" checked={mode === "all"} onChange={() => setMode("all")}
                  data-testid="apply-mode-all" className="mt-1" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Sync all stock to my count</p>
                  <p className="text-xs text-slate-500 mt-0.5">Updates {counted.length} product{counted.length === 1 ? "" : "s"}. {stockVariances.length} differ from current system stock — those will be logged as <span className="font-mono text-[10px] bg-slate-100 px-1 rounded">stocktake_variance</span> for the audit trail.</p>
                </div>
              </label>
              <label className="flex items-start gap-2 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-[#FAFAFA]">
                <input type="radio" name="mode" value="matches_only" checked={mode === "matches_only"} onChange={() => setMode("matches_only")}
                  data-testid="apply-mode-matches" className="mt-1" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Only sync lines that match system stock</p>
                  <p className="text-xs text-slate-500 mt-0.5">Skips the {stockVariances.length} line{stockVariances.length === 1 ? "" : "s"} that differ from system — leaves you to review them later.</p>
                </div>
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-slate-200 rounded-full px-5">Close</Button>
            <Button onClick={apply} disabled={saving || counted.length === 0}
              data-testid="apply-count-submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5">
              {saving ? "Applying..." : "Apply count"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
